import React, { useEffect, useMemo, useState } from 'react';
import {
  browserSupportsWebAuthn,
  WebAuthnError,
} from '@simplewebauthn/browser';
import { api, type AuthState } from '../api';
import { beginPasskeyAuthentication, beginPasskeyRegistration } from '../webauthn';

interface Props {
  authState: AuthState;
  onAuthenticated: () => Promise<unknown>;
}

type LoginStep = 'bootstrap' | 'password' | 'pending_passkey' | 'pending_enrollment';

export function LoginPage({ authState, onAuthenticated }: Props) {
  const [username, setUsername] = useState(authState.user?.username ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>(deriveStep(authState));
  const passkeysSupported = useMemo(() => browserSupportsWebAuthn(), []);

  useEffect(() => {
    setStep(deriveStep(authState));

    if (authState.user?.username) {
      setUsername(authState.user.username);
    }
  }, [authState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (step === 'bootstrap') {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        await api.auth.bootstrap(username, password);
        setMessage('Admin account created. Create a passkey to finish setup.');
        setStep('pending_enrollment');
      } else {
        const result = await api.auth.loginPassword(username, password);

        if (result.data.requiresPasskeyEnrollment) {
          setMessage('No passkey is registered yet. Create one now to finish sign in.');
          setStep('pending_enrollment');
        } else {
          setMessage('Password accepted. Use your passkey to finish signing in.');
          setStep('pending_passkey');
        }
      }

      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyEnrollment = async () => {
    if (!passkeysSupported) {
      setError('This browser does not support passkeys');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      const options = await api.auth.getPasskeyRegistrationOptions();
      const response = await beginPasskeyRegistration(options.data);
      const result = await api.auth.verifyPasskeyRegistration(response);

      if (result.data.authenticated) {
        await onAuthenticated();
      }
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!passkeysSupported) {
      setError('This browser does not support passkeys');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      const options = await api.auth.getPasskeyAuthenticationOptions();
      const response = await beginPasskeyAuthentication(options.data);
      const result = await api.auth.verifyPasskeyAuthentication(response);

      if (result.data.authenticated) {
        await onAuthenticated();
      }
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    setError('');
    setMessage('');
    setPassword('');
    setConfirmPassword('');
    setLoading(true);

    try {
      await api.auth.logout();
      await onAuthenticated();
    } finally {
      setLoading(false);
    }
  };

  const showPasswordForm = step === 'bootstrap' || step === 'password';

  return (
    <div className="login-page">
      <div className="login-card">
        {showPasswordForm && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your username"
                autoFocus
                autoComplete="username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{step === 'bootstrap' ? 'Create Password' : 'Password'}</label>
              <input
                id="password"
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={step === 'bootstrap' ? 'Create a strong password' : 'Enter your password'}
                autoComplete={step === 'bootstrap' ? 'new-password' : 'current-password'}
                required
              />
            </div>

            {step === 'bootstrap' && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  required
                />
              </div>
            )}

            {step === 'bootstrap' && (
              <p className="form-helper">
                Create the first admin account, then register a passkey on this device.
              </p>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Working...' : step === 'bootstrap' ? 'Create Admin Account' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'pending_enrollment' && (
          <div className="login-actions">
            <p className="form-helper">
              Create a passkey on this device to finish setting up your secure sign-in.
            </p>
            <button type="button" className="btn btn-primary" onClick={handlePasskeyEnrollment} disabled={loading}>
              {loading ? 'Creating Passkey...' : 'Create Passkey'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={handleStartOver} disabled={loading}>
              Start Over
            </button>
          </div>
        )}

        {step === 'pending_passkey' && (
          <div className="login-actions">
            <p className="form-helper">
              Your password was accepted. Use your registered passkey to finish signing in,
              or register a new passkey on this device.
            </p>
            <button type="button" className="btn btn-primary" onClick={handlePasskeyLogin} disabled={loading}>
              {loading ? 'Checking Passkey...' : 'Use Passkey'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handlePasskeyEnrollment} disabled={loading}>
              {loading ? 'Registering...' : 'Register This Device'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={handleStartOver} disabled={loading}>
              Start Over
            </button>
          </div>
        )}

        {message && <p className="success-msg">{message}</p>}
        {error && <p className="error-msg">{error}</p>}
      </div>
    </div>
  );
}

function deriveStep(authState: AuthState): LoginStep {
  if (authState.bootstrapRequired) {
    return 'bootstrap';
  }

  if (authState.sessionStage === 'pending_passkey_enrollment') {
    return 'pending_enrollment';
  }

  if (authState.sessionStage === 'pending_mfa') {
    return 'pending_passkey';
  }

  return 'password';
}

function getAuthErrorMessage(error: unknown): string {
  if (error instanceof WebAuthnError) {
    return error.message;
  }

  if (error instanceof DOMException) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Authentication failed';
}
